import React, { useState } from 'react';
import { X, Edit2, Trash2 } from 'lucide-react';
import { HexColorPicker } from 'react-colorful';
import { supabase } from '../lib/supabase';
import type { Vehicle } from '../types';

interface VehicleFormProps {
  vehicles: Vehicle[];
  onClose: () => void;
  onSave: (vehicle: Vehicle) => void;
  onUpdate: () => void;
}

const VehicleForm: React.FC<VehicleFormProps> = ({ vehicles, onClose, onSave, onUpdate }) => {
  const [formData, setFormData] = useState({
    name: '',
    capacity: 4,
    color: '#4A90E2'
  });
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState<Vehicle | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        alert('로그인이 필요합니다.');
        return;
      }

      if (editingVehicle) {
        const { error } = await supabase
          .from('vehicles')
          .update({
            name: formData.name,
            capacity: formData.capacity,
            color: formData.color
          })
          .eq('id', editingVehicle.id);

        if (error) throw error;
      } else {
        const { data, error } = await supabase
          .from('vehicles')
          .insert([{
            ...formData,
            user_id: user.id
          }])
          .select()
          .single();

        if (error) throw error;
        if (data) onSave(data);
      }

      onUpdate();
      setEditingVehicle(null);
      setFormData({ name: '', capacity: 4, color: '#4A90E2' });
    } catch (error) {
      console.error('Error saving vehicle:', error);
      alert('차량 정보 저장 중 오류가 발생했습니다.');
    }
  };

  const handleDelete = async (vehicle: Vehicle) => {
    if (!confirm('정말 삭제하시겠습니까?')) return;

    try {
      const { error } = await supabase
        .from('vehicles')
        .delete()
        .eq('id', vehicle.id);

      if (error) throw error;
      onUpdate();
    } catch (error) {
      console.error('Error deleting vehicle:', error);
      alert('차량 삭제 중 오류가 발생했습니다.');
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto relative">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
        >
          <X size={24} />
        </button>

        <h2 className="text-2xl font-bold mb-6">차량 관리</h2>

        <div className="grid grid-cols-2 gap-6">
          <div>
            <h3 className="text-lg font-semibold mb-4">
              {editingVehicle ? '차량 수정' : '새 차량 등록'}
            </h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  차량 이름
                </label>
                <input
                  type="text"
                  required
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  탑승 인원
                </label>
                <input
                  type="number"
                  required
                  min={1}
                  max={45}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  value={formData.capacity}
                  onChange={(e) => setFormData(prev => ({ ...prev, capacity: parseInt(e.target.value) }))}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  차량 색상
                </label>
                <div className="mt-1 flex items-center gap-2">
                  <button
                    type="button"
                    className="w-10 h-10 rounded-md border border-gray-300 shadow-sm"
                    style={{ backgroundColor: formData.color }}
                    onClick={() => setShowColorPicker(!showColorPicker)}
                  />
                  <span className="text-sm text-gray-600">
                    {formData.color.toUpperCase()}
                  </span>
                </div>
                {showColorPicker && (
                  <div className="absolute mt-2">
                    <HexColorPicker
                      color={formData.color}
                      onChange={(color) => setFormData(prev => ({ ...prev, color }))}
                    />
                  </div>
                )}
              </div>

              <div className="flex justify-end space-x-3 mt-8">
                {editingVehicle && (
                  <button
                    type="button"
                    onClick={() => {
                      setEditingVehicle(null);
                      setFormData({ name: '', capacity: 4, color: '#4A90E2' });
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                  >
                    취소
                  </button>
                )}
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  {editingVehicle ? '수정' : '저장'}
                </button>
              </div>
            </form>
          </div>

          <div>
            <h3 className="text-lg font-semibold mb-4">등록된 차량</h3>
            <div className="space-y-3">
              {vehicles.map(vehicle => (
                <div
                  key={vehicle.id}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <div
                      className="w-6 h-6 rounded-full"
                      style={{ backgroundColor: vehicle.color }}
                    />
                    <div>
                      <div className="font-medium">{vehicle.name}</div>
                      <div className="text-sm text-gray-500">
                        탑승 인원: {vehicle.capacity}명
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => {
                        setEditingVehicle(vehicle);
                        setFormData({
                          name: vehicle.name,
                          capacity: vehicle.capacity,
                          color: vehicle.color
                        });
                      }}
                      className="p-2 text-gray-500 hover:text-gray-700 rounded-full hover:bg-gray-100"
                    >
                      <Edit2 size={16} />
                    </button>
                    <button
                      onClick={() => handleDelete(vehicle)}
                      className="p-2 text-red-500 hover:text-red-700 rounded-full hover:bg-red-50"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VehicleForm;